import { FC, Fragment, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { Table, TableHead, TableRow, TableCell, TableBody, Tooltip, TextField, IconButton } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { PaperTitle, PaperTitleText, PaperContent } from '../../primitive/Paper';
import {
  classifyMetric,
  LinkPrefetchReportData,
  LinkPrefetchReportThreshold,
  MetricCategoryLabel,
} from '../../../../hooks/useLinkPrefetchReport';
import SpeculationRule from '../../../../entity/SpeculationRule';

interface PageData {
  url: string;
  controlPv: number;
  experimentalPv: number;
  controlDCL: string;
  controlLCP: string;
  testDCL: string;
  testLCP: string;
  dclImprovement: string;
  rawControlLCP: number;
  rawTestLCP: number;
}

interface GroupData {
  id: number;
  groupName: string;
  patterns: string[];
  totalControlPv: number;
  totalExperimentalPv: number;
  controlDCL: string;
  controlLCP: string;
  testDCL: string;
  testLCP: string;
  dclImprovement: string;
  lcpImprovement: string;
  rawControlLCP: number;
  rawTestLCP: number;
  pageDataList: PageData[];
}

type Props = {
  reportData: LinkPrefetchReportData;
  reportThreshold: LinkPrefetchReportThreshold;
  speculationRule: SpeculationRule;
};

const ReportTable: FC<Props> = ({ reportData, reportThreshold, speculationRule }) => {
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const itemsPerPage = 10;
  const pvThreshold = reportThreshold.pv;

  const calculateImprovementPercentage = (control: number, test: number): string => {
    if (control === 0) {
      return 'N/A';
    }
    const improvement = ((test - control) / control) * 100;
    const rounded = Math.round(improvement);
    return rounded > 0 ? `+${rounded}%` : `${rounded}%`;
  };

  // パターンをグループに分類
  const groupedData = useMemo(() => {
    const groups: GroupData[] = [];
    const grouping = speculationRule.whitelistPathGrouping || [];

    // 各グループについて処理
    grouping.forEach(([groupName, patterns], groupIndex) => {
      const pageDataList: PageData[] = [];
      let totalControlDcl = 0;
      let totalControlLcp = 0;
      let totalExperimentalDcl = 0;
      let totalExperimentalLcp = 0;
      let totalControlPv = 0;
      let totalExperimentalPv = 0;

      // グループのパターンに一致するreportDataのパターンを集計
      patterns.forEach((pattern) => {
        if (reportData.patterns[pattern]) {
          const data = reportData.patterns[pattern];

          // ページデータを作成
          pageDataList.push({
            url: pattern,
            controlPv: data.control.pv,
            experimentalPv: data.experimental.pv,
            controlDCL: `${(data.control.dcl / 1000).toFixed(2)}秒`,
            controlLCP: `${(data.control.lcp / 1000).toFixed(2)}秒`,
            testDCL: `${(data.experimental.dcl / 1000).toFixed(2)}秒`,
            testLCP: `${(data.experimental.lcp / 1000).toFixed(2)}秒`,
            dclImprovement: calculateImprovementPercentage(data.control.dcl, data.experimental.dcl),
            rawControlLCP: data.control.lcp,
            rawTestLCP: data.experimental.lcp,
          });

          // グループ集計値を計算
          totalControlPv += data.control.pv;
          totalExperimentalPv += data.experimental.pv;
          totalControlDcl += data.control.dcl * data.control.pv;
          totalControlLcp += data.control.lcp * data.control.pv;
          totalExperimentalDcl += data.experimental.dcl * data.experimental.pv;
          totalExperimentalLcp += data.experimental.lcp * data.experimental.pv;
        }
      });

      if (pageDataList.length > 0) {
        // PV降順でソート
        pageDataList.sort((a, b) => b.experimentalPv - a.experimentalPv);

        // 平均値を計算
        const avgControlDcl = totalControlPv > 0 ? totalControlDcl / totalControlPv : 0;
        const avgControlLcp = totalControlPv > 0 ? totalControlLcp / totalControlPv : 0;
        const avgExperimentalDcl = totalExperimentalPv > 0 ? totalExperimentalDcl / totalExperimentalPv : 0;
        const avgExperimentalLcp = totalExperimentalPv > 0 ? totalExperimentalLcp / totalExperimentalPv : 0;

        groups.push({
          id: groupIndex,
          groupName,
          patterns,
          totalControlPv,
          totalExperimentalPv,
          controlDCL: `${(avgControlDcl / 1000).toFixed(2)}秒`,
          controlLCP: `${(avgControlLcp / 1000).toFixed(2)}秒`,
          testDCL: `${(avgExperimentalDcl / 1000).toFixed(2)}秒`,
          testLCP: `${(avgExperimentalLcp / 1000).toFixed(2)}秒`,
          dclImprovement: calculateImprovementPercentage(avgControlDcl, avgExperimentalDcl),
          lcpImprovement: calculateImprovementPercentage(avgControlLcp, avgExperimentalLcp),
          rawControlLCP: avgControlLcp,
          rawTestLCP: avgExperimentalLcp,
          pageDataList,
        });
      }
    });

    // PV降順でグループをソート
    groups.sort((a, b) => b.totalExperimentalPv - a.totalExperimentalPv);

    return groups;
  }, [reportData, speculationRule]);

  // 検索でフィルタリング
  const filteredGroupData = useMemo(() => {
    if (!searchText) return groupedData;

    const lowerSearchText = searchText.toLowerCase();

    return groupedData
      .map((group) => {
        // グループ名が検索テキストに一致する場合、グループ全体を表示
        if (group.groupName.toLowerCase().includes(lowerSearchText)) {
          return group;
        }

        // グループ名が一致しない場合は、ページURLでフィルタリング
        return {
          ...group,
          pageDataList: group.pageDataList.filter((page) => page.url.toLowerCase().includes(lowerSearchText)),
        };
      })
      .filter((group) => group.pageDataList.length > 0);
  }, [groupedData, searchText]);

  const totalItems = filteredGroupData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedGroupData = filteredGroupData.slice(startIndex, endIndex);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setCurrentPage(1); // 検索時はページを1にリセット
  };

  const toggleGroup = (id: number) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getImprovementColor = (improvement: string) => {
    if (improvement.startsWith('-')) return '#4CAF50';
    if (improvement.startsWith('+')) return '#F44336';
    return '#FFC107';
  };

  const renderLcpCategory = (rawControlLCP: number, rawTestLCP: number) => {
    const controlCategory = classifyMetric(rawControlLCP, reportThreshold.lcpWarn, reportThreshold.lcpPoor);
    const testCategory = classifyMetric(rawTestLCP, reportThreshold.lcpWarn, reportThreshold.lcpPoor);
    const label =
      controlCategory === testCategory
        ? `「${MetricCategoryLabel[controlCategory]}」範囲内`
        : `「${MetricCategoryLabel[controlCategory]}」→「${MetricCategoryLabel[testCategory]}」`;
    const color =
      controlCategory === testCategory
        ? controlCategory === 0
          ? getImprovementColor('-')
          : controlCategory === 1
          ? getImprovementColor('')
          : getImprovementColor('+')
        : testCategory < controlCategory
        ? getImprovementColor('-')
        : getImprovementColor('+');
    return <ImprovementText color={color}>{label}</ImprovementText>;
  };

  return (
    <Container>
      <PaperTitle>
        <PaperTitleText>リンクグループ別A/Bテスト結果</PaperTitleText>
      </PaperTitle>
      <PaperContent>
        <SearchContainer>
          <TextField
            placeholder="リンクグループ/ページURLで検索"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            style={{ width: '400px' }}
          />
        </SearchContainer>
        <PaginationContainer>
          <PaginationInfo>
            全{totalItems}グループ中 {totalItems > 0 ? startIndex + 1 : 0}-{endIndex}グループを表示 (有効PV降順)
          </PaginationInfo>
          <PaginationButtons>
            <PageButton onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              ≪
            </PageButton>
            <PageButton onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
              ＜
            </PageButton>
            <PageInfo>
              {currentPage} / {totalPages || 1}
            </PageInfo>
            <PageButton onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages}>
              ＞
            </PageButton>
            <PageButton onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>
              ≫
            </PageButton>
          </PaginationButtons>
        </PaginationContainer>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>
                <UrlWithPv>
                  リンクグループ
                  <Tooltip title="速度改善ツールが起動して正常計測されたページビュー数です。対象OS・ブラウザでのアクセス時にのみカウントされ、異常値は除外されます。">
                    <PvText>
                      (有効PV
                      <HelpIcon style={{ fontSize: 16, marginBottom: -3, paddingLeft: 3 }} />)
                    </PvText>
                  </Tooltip>
                </UrlWithPv>
                <PatterNoticeText>※グループ内の各パスパターンを展開して確認できます</PatterNoticeText>
              </TableCell>
              <TableCell align="center" colSpan={2}>
                未設定グループ (10%)
              </TableCell>
              <TableCell rowSpan={2} align="center" style={{ width: '2%' }}></TableCell>
              <TableCell align="center" colSpan={2}>
                設定グループ (90%)
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center" style={{ width: '10%' }}>
                DCL
              </TableCell>
              <TableCell align="center" style={{ width: '10%' }}>
                LCP
              </TableCell>
              <TableCell align="center" style={{ width: '10%', minWidth: '130px' }}>
                DCL
              </TableCell>
              <TableCell align="center" style={{ width: '15%', minWidth: '195px' }}>
                LCP
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGroupData.map((group, groupIndex) => {
              const isExpanded = expandedGroups.has(group.id);
              return (
                <Fragment key={group.id}>
                  <GroupRow onClick={() => toggleGroup(group.id)}>
                    <TableCell>
                      <UrlWithPv>
                        <IconButton size="small" style={{ padding: 4, marginRight: 4 }}>
                          {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </IconButton>
                        <GroupNameText>{group.groupName}</GroupNameText>
                        <PvText>({group.totalExperimentalPv.toLocaleString()})</PvText>
                      </UrlWithPv>
                    </TableCell>
                    <TableCell align="center">{group.controlDCL}</TableCell>
                    <TableCell align="center">{group.controlLCP}</TableCell>
                    <TableCell align="center">→</TableCell>
                    <TableCell align="center">
                      <MetricWithImprovement>
                        <span>{group.testDCL}</span>
                        {group.totalControlPv === 0 || group.totalExperimentalPv < pvThreshold ? (
                          <Tooltip title="有効PVが不足しています">
                            <div style={{ display: 'flex', paddingLeft: '10px' }}>
                              <NeutralText>データ不足</NeutralText>
                              <HelpIcon style={{ fontSize: 16, marginTop: 5, paddingLeft: 3 }} />
                            </div>
                          </Tooltip>
                        ) : (
                          <ImprovementText color={getImprovementColor(group.dclImprovement)}>
                            {group.dclImprovement}
                          </ImprovementText>
                        )}
                      </MetricWithImprovement>
                    </TableCell>
                    <TableCell align="center">
                      <MetricWithImprovement>
                        <span>{group.testLCP}</span>
                        {group.totalControlPv === 0 || group.totalExperimentalPv < pvThreshold ? (
                          <Tooltip title="有効PVが不足しています">
                            <div style={{ display: 'flex', paddingLeft: '10px' }}>
                              <NeutralText>データ不足</NeutralText>
                              <HelpIcon style={{ fontSize: 16, marginTop: 5, paddingLeft: 3 }} />
                            </div>
                          </Tooltip>
                        ) : (
                          renderLcpCategory(group.rawControlLCP, group.rawTestLCP)
                        )}
                      </MetricWithImprovement>
                    </TableCell>
                  </GroupRow>
                  {isExpanded &&
                    group.pageDataList.map((page, pageIndex) => (
                      <DetailRow key={`page-${groupIndex}-${pageIndex}`}>
                        <TableCell>
                          <UrlWithPv style={{ paddingLeft: '40px' }}>
                            <UrlText>{page.url}</UrlText>
                            <PvText>({page.experimentalPv.toLocaleString()})</PvText>
                          </UrlWithPv>
                        </TableCell>
                        <TableCell align="center">{page.controlDCL}</TableCell>
                        <TableCell align="center">{page.controlLCP}</TableCell>
                        <TableCell align="center">→</TableCell>
                        <TableCell align="center">
                          <MetricWithImprovement>
                            <span>{page.testDCL}</span>
                            {page.controlPv === 0 || page.experimentalPv < pvThreshold ? (
                              <Tooltip title="有効PVが不足しています">
                                <div style={{ display: 'flex', paddingLeft: '10px' }}>
                                  <NeutralText>データ不足</NeutralText>
                                  <HelpIcon style={{ fontSize: 16, marginTop: 5, paddingLeft: 3 }} />
                                </div>
                              </Tooltip>
                            ) : (
                              <ImprovementText color={getImprovementColor(page.dclImprovement)}>
                                {page.dclImprovement}
                              </ImprovementText>
                            )}
                          </MetricWithImprovement>
                        </TableCell>
                        <TableCell align="center">
                          <MetricWithImprovement>
                            <span>{page.testLCP}</span>
                            {page.controlPv === 0 || page.experimentalPv < pvThreshold ? (
                              <Tooltip title="有効PVが不足しています">
                                <div style={{ display: 'flex', paddingLeft: '10px' }}>
                                  <NeutralText>データ不足</NeutralText>
                                  <HelpIcon style={{ fontSize: 16, marginTop: 5, paddingLeft: 3 }} />
                                </div>
                              </Tooltip>
                            ) : (
                              renderLcpCategory(page.rawControlLCP, page.rawTestLCP)
                            )}
                          </MetricWithImprovement>
                        </TableCell>
                      </DetailRow>
                    ))}
                </Fragment>
              );
            })}
          </TableBody>
        </StyledTable>
      </PaperContent>
    </Container>
  );
};

const Container = styled('div')({
  width: '100%',
});

const StyledTable = styled(Table)({
  border: '1px solid #e6e7e8',
  borderRadius: '4px',
  borderCollapse: 'separate',
});

const UrlWithPv = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const UrlText = styled('div')({
  color: '#2196F3',
  fontWeight: 500,
});

const GroupNameText = styled('div')({
  color: '#333',
  fontWeight: 700,
  fontSize: '14px',
});

const PvText = styled('div')({
  fontSize: '12px',
  color: '#666',
  whiteSpace: 'nowrap',
});

const PatterNoticeText = styled('div')({
  fontSize: '10px',
  color: '#666',
  marginLeft: '4px',
  marginTop: '4px',
});

const MetricWithImprovement = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px',
});

const ImprovementText = styled('div')<{ color: string }>(({ color }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color,
  padding: '2px 6px',
  borderRadius: '4px',
  border: `1px solid ${color}`,
  backgroundColor: color === '#4CAF50' ? '#E8F5E9' : color === '#F44336' ? '#FFEBEE' : '#FFF8E1',
  display: 'inline-block',
}));

const NeutralText = styled('div')({
  fontSize: '10px',
  fontWeight: 600,
  color: '#9E9E9E',
  padding: '2px 6px',
  borderRadius: '4px',
  border: '1px solid #9E9E9E',
  backgroundColor: '#F5F5F5',
  display: 'inline-block',
});

const SearchContainer = styled('div')({
  marginTop: '20px',
  marginBottom: '16px',
});

const PaginationContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
});

const PaginationInfo = styled('div')({
  fontSize: '14px',
  color: '#666',
});

const PaginationButtons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const PageButton = styled('button')<{ disabled?: boolean }>(({ disabled }) => ({
  padding: '4px 12px',
  fontSize: '14px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  backgroundColor: disabled ? '#f5f5f5' : '#fff',
  color: disabled ? '#ccc' : '#333',
  cursor: disabled ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: disabled ? '#f5f5f5' : '#f0f0f0',
  },
}));

const PageInfo = styled('div')({
  padding: '0 12px',
  fontSize: '14px',
  color: '#333',
});

const GroupRow = styled(TableRow)({
  backgroundColor: '#f5f5f5',
  '&:hover': {
    backgroundColor: '#eeeeee',
  },
  cursor: 'pointer',
});

const DetailRow = styled(TableRow)({
  backgroundColor: '#fafafa',
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
});

export default ReportTable;
